import { useEffect, useState } from "react";
import { ArrowRight, Move, Pencil, Printer, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui-shim";
import {
  CONDITIONS, LocationRecord, MOVE_REASONS, MoveReason, downloadAuthed,
  itemsApi, taxonomyApi,
} from "@/lib/catalog-api";
import { toast } from "sonner";

export function BulkActionsBar({ selected, onClear, onAfterAction, locations }: {
  selected: string[];
  onClear: () => void;
  onAfterAction: () => void;
  locations: LocationRecord[];
}) {
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  if (selected.length === 0) return null;

  async function printLabels() {
    setPrinting(true);
    try {
      await downloadAuthed("/items/labels/bulk", `wardrobe-labels-${selected.length}.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_ids: selected }),
      });
      toast.success(`${selected.length} label${selected.length === 1 ? "" : "s"} downloaded.`);
    } catch (e: any) {
      toast.error(e?.message || "Could not generate labels.");
    } finally { setPrinting(false); }
  }

  return (
    <>
      <div data-testid="bulk-actions-bar"
        className="sticky bottom-4 left-0 right-0 z-30 mx-auto mt-6 w-fit max-w-full bg-foreground text-ivory shadow-lg flex items-center gap-1 px-2 py-2">
        <span className="px-3 text-sm font-medium" data-testid="bulk-selected-count">
          {selected.length} selected
        </span>
        <Button size="sm" variant="ghost" onClick={() => setMoveOpen(true)}
          data-testid="bulk-move-btn"
          className="h-9 rounded-none text-ivory hover:bg-ivory/10">
          <Move className="w-4 h-4 mr-1.5" /> Move
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)}
          data-testid="bulk-edit-btn"
          className="h-9 rounded-none text-ivory hover:bg-ivory/10">
          <Pencil className="w-4 h-4 mr-1.5" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={printLabels} disabled={printing}
          data-testid="bulk-labels-btn"
          className="h-9 rounded-none text-ivory hover:bg-ivory/10">
          <Printer className="w-4 h-4 mr-1.5" /> {printing ? "Building PDF…" : "Print labels"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setDeleteOpen(true)}
          data-testid="bulk-delete-btn"
          className="h-9 rounded-none text-red-300 hover:bg-red-500/20 hover:text-red-200">
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete
        </Button>
        <button type="button" onClick={onClear}
          data-testid="bulk-clear-btn"
          aria-label="Clear selection"
          className="h-9 px-3 hover:bg-ivory/10">
          <X className="w-4 h-4" />
        </button>
      </div>
      <BulkMoveDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        locations={locations}
        selectedIds={selected}
        onMoved={() => { setMoveOpen(false); onClear(); onAfterAction(); }}
      />
      <BulkDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        selectedIds={selected}
        onDeleted={() => { setDeleteOpen(false); onClear(); onAfterAction(); }}
      />
      <BulkEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        selectedIds={selected}
        onSaved={() => { setEditOpen(false); onClear(); onAfterAction(); }}
      />
    </>
  );
}

function BulkEditDialog({ open, onClose, selectedIds, onSaved }: {
  open: boolean; onClose: () => void; selectedIds: string[]; onSaved: () => void;
}) {
  const FIELDS = [
    { key: "current_location_name", label: "Location" },
    { key: "location_type", label: "Location type" },
    { key: "box", label: "Box" },
    { key: "category", label: "Category" },
    { key: "brand", label: "Brand" },
    { key: "condition", label: "Condition" },
  ];
  const [field, setField] = useState("location_type");
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [opts, setOpts] = useState<{ location_types: string[]; categories: string[]; brands: string[]; locations: string[] }>({ location_types: [], categories: [], brands: [], locations: [] });

  useEffect(() => {
    if (!open) return;
    setField("location_type"); setValue("");
    Promise.all([
      taxonomyApi.locationTypes().catch(() => ({ location_types: [] })),
      taxonomyApi.categories().catch(() => ({ categories: [] })),
      taxonomyApi.brands().catch(() => ({ brands: [] })),
      import("@/lib/catalog-api").then((m) => m.locationsApi.list()).catch(() => []),
    ]).then(([lt, c, b, locs]: any) => {
      setOpts({
        location_types: lt.location_types || [],
        categories: c.categories || [],
        brands: b.brands || [],
        locations: (locs || []).map((l: any) => l.name),
      });
    });
  }, [open]);

  const listId = `bulk-edit-opts`;
  const options = field === "location_type" ? opts.location_types
    : field === "category" ? opts.categories
    : field === "brand" ? opts.brands
    : field === "current_location_name" ? opts.locations
    : field === "condition" ? [...CONDITIONS] : [];

  async function submit() {
    setSubmitting(true);
    try {
      const r = await itemsApi.bulkEdit(selectedIds, field, value.trim() || null);
      toast.success(`Updated ${r.updated} item${r.updated === 1 ? "" : "s"}.`);
      onSaved();
    } catch (e: any) {
      toast.error(e?.message || "Bulk edit failed.");
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="bulk-edit-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit {selectedIds.length} item{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Field</label>
            <select data-testid="bulk-edit-field" value={field}
              onChange={(e) => { setField(e.target.value); setValue(""); }}
              className="h-10 w-full border border-border bg-background px-2 text-sm">
              {FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">New value</label>
            {field === "condition" ? (
              <select data-testid="bulk-edit-value" value={value} onChange={(e) => setValue(e.target.value)}
                className="h-10 w-full border border-border bg-background px-2 text-sm">
                <option value="">Choose…</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <>
                <input data-testid="bulk-edit-value" list={listId} value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={field === "box" ? "e.g. B12" : "Pick or type"}
                  className="h-10 w-full border border-border bg-background px-3 text-sm" />
                <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>
              </>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">Applies to all {selectedIds.length} selected. Leave blank to clear the field.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="bulk-edit-submit"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
            {submitting ? "Saving…" : "Apply to all"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeleteDialog({ open, onClose, selectedIds, onDeleted }: {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onDeleted: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  async function submit() {
    setSubmitting(true);
    try {
      const r = await itemsApi.bulkDelete(selectedIds);
      toast.success(`Deleted ${r.deleted} item${r.deleted === 1 ? "" : "s"}.`);
      onDeleted();
    } catch (e: any) {
      toast.error(e?.message || "Bulk delete failed.");
    } finally { setSubmitting(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="bulk-delete-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Delete {selectedIds.length} item{selectedIds.length === 1 ? "" : "s"}?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          This permanently removes the selected item{selectedIds.length === 1 ? "" : "s"} and their photos. This can't be undone. (Tip: use this to clear the catalog before a clean re-import.)
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="bulk-delete-submit"
            className="bg-red-600 hover:bg-red-700 text-white rounded-none">
            {submitting ? "Deleting…" : <><Trash2 className="w-4 h-4 mr-2" /> Delete {selectedIds.length}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkMoveDialog({ open, onClose, locations, selectedIds, onMoved }: {
  open: boolean;
  onClose: () => void;
  locations: LocationRecord[];
  selectedIds: string[];
  onMoved: () => void;
}) {
  const [locId, setLocId] = useState("");
  const [reason, setReason] = useState<MoveReason>("Storage rotation");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!locId) { toast.error("Pick a target location."); return; }
    setSubmitting(true);
    try {
      const r = await itemsApi.bulkMove({
        item_ids: selectedIds,
        to_location_id: locId,
        reason,
        notes: notes.trim() || undefined,
      });
      const dest = locations.find((l) => l._id === locId);
      const destName = dest?.name || "the new location";
      if (r.moved === 0) {
        toast.success(`Already at ${destName} — nothing to move.`);
      } else {
        const noun = r.moved === 1 ? "item" : "items";
        const skipNote = r.skipped > 0 ? ` (${r.skipped} already there)` : "";
        toast.success(`Moved ${r.moved} ${noun} to ${destName}${skipNote}.`);
      }
      onMoved();
    } catch (e: any) {
      toast.error(e?.message || "Bulk move failed.");
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="bulk-move-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Move {selectedIds.length} item{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Move to</label>
            <select data-testid="bulk-move-location"
              className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
              value={locId} onChange={(e) => setLocId(e.target.value)}>
              <option value="">Pick a location…</option>
              {locations.filter((l) => !l.archived).map((l) => (
                <option key={l._id} value={l._id}>{l.name} — {l.type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Reason</label>
            <select data-testid="bulk-move-reason"
              className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
              value={reason} onChange={(e) => setReason(e.target.value as MoveReason)}>
              {MOVE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="bulk-move-submit"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
            {submitting ? "Moving…" : <>Move {selectedIds.length} <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
